import React, { useState, useEffect } from 'react';
import { Bookmark, Search, Grid, List, Trash2, ExternalLink, MapPin, Euro, Calendar, Filter, TrendingUp, Star, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { formatCurrency, formatTimeAgo } from '../utils/formatters';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export default function SavedListingsPage() {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('grid'); // 'grid' or 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('savedAt'); // 'savedAt', 'score', 'price'
    const [filterTag, setFilterTag] = useState('all');

    useEffect(() => {
        fetchSavedListings();
    }, [sortBy]);

    const fetchSavedListings = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/api/saved-listings`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { sortBy }
            });

            if (response.data.success) {
                setListings(response.data.listings);
            }
        } catch (error) {
            console.error('Failed to fetch saved listings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (listingId) => {
        if (!confirm('Remove this listing from saved?')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/saved-listings/${listingId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setListings(listings.filter(l => l._id !== listingId));
        } catch (error) {
            console.error('Failed to delete listing:', error);
        }
    };

    const filteredListings = listings.filter(listing => {
        const matchesSearch = listing.propertyData?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            listing.propertyData?.location?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTag = filterTag === 'all' || listing.tags?.includes(filterTag);
        return matchesSearch && matchesTag;
    });

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-600';
        if (score >= 60) return 'text-yellow-600';
        return 'text-gray-600';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="relative w-16 h-16 mx-auto mb-4">
                        <div className="absolute inset-0 border-4 border-emerald-200 rounded-full"></div>
                        <div className="absolute inset-0 border-4 border-t-emerald-600 rounded-full animate-spin"></div>
                    </div>
                    <p className="text-gray-600">Loading saved listings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                                <Bookmark className="w-8 h-8 text-emerald-600" />
                                Saved Listings
                            </h1>
                            <p className="text-gray-600">Your saved property analyses ({listings.length})</p>
                        </div>

                        {/* Compare Button (shows if 2+ listings) */}
                        {listings.length >= 2 && (
                            <a
                                href="/dashboard/comparison"
                                className="px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
                            >
                                <BarChart3 className="w-5 h-5" />
                                Compare Properties
                            </a>
                        )}
                    </div>
                </div>

                {/* Controls */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        {/* Search */}
                        <div className="relative flex-1 w-full md:max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by title or location..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            />
                        </div>

                        {/* Sort */}
                        <div className="flex items-center gap-3">
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            >
                                <option value="savedAt">Recently Saved</option>
                                <option value="score">Highest Score</option>
                                <option value="price">Price</option>
                            </select>

                            {/* View Toggle */}
                            <div className="flex bg-gray-100 rounded-lg p-1">
                                <button
                                    onClick={() => setView('grid')}
                                    className={`p-2 rounded ${view === 'grid' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                                >
                                    <Grid className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setView('list')}
                                    className={`p-2 rounded ${view === 'list' ? 'bg-white shadow-sm' : 'text-gray-600'}`}
                                >
                                    <List className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Empty State */}
                {filteredListings.length === 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                        <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">No saved listings yet</h3>
                        <p className="text-gray-600 mb-6">
                            {searchTerm ? 'No listings match your search.' : 'Start analyzing properties and save your favorites!'}
                        </p>
                        {!searchTerm && (
                            <a
                                href="/dashboard/listing-analysis"
                                className="inline-block px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                            >
                                Analyze Properties
                            </a>
                        )}
                    </div>
                )}

                {/* Grid View */}
                {view === 'grid' && filteredListings.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredListings.map((listing) => (
                            <div
                                key={listing._id}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                            >
                                {/* Image or Placeholder */}
                                {listing.propertyData?.images?.[0] ? (
                                    <img
                                        src={listing.propertyData.images[0]}
                                        alt={listing.propertyData.title}
                                        className="w-full h-48 object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
                                        <MapPin className="w-12 h-12 text-gray-400" />
                                    </div>
                                )}

                                <div className="p-4">
                                    {/* Score Badge */}
                                    <div className="flex items-center justify-between mb-2">
                                        <div className={`text-3xl font-bold ${getScoreColor(listing.analysis?.overallScore || 0)}`}>
                                            {listing.analysis?.overallScore || 0}
                                            <span className="text-sm text-gray-500">/100</span>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(listing._id)}
                                            className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600 transition-colors"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>

                                    {/* Title */}
                                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                                        {listing.propertyData?.title || 'Untitled Property'}
                                    </h3>

                                    {/* Location */}
                                    {listing.propertyData?.location && (
                                        <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                                            <MapPin className="w-4 h-4" />
                                            <span className="truncate">{listing.propertyData.location}</span>
                                        </div>
                                    )}

                                    {/* Price */}
                                    {listing.propertyData?.price && (
                                        <div className="flex items-center gap-1 text-lg font-bold text-gray-900 mb-3">
                                            <Euro className="w-5 h-5" />
                                            {formatCurrency(listing.propertyData.price)}
                                        </div>
                                    )}

                                    {/* Saved Date */}
                                    <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                                        <Calendar className="w-3 h-3" />
                                        Saved {formatTimeAgo(listing.savedAt)}
                                    </div>

                                    {/* View Button */}
                                    <a
                                        href={listing.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full px-4 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        View Analysis
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* List View */}
                {view === 'list' && filteredListings.length > 0 && (
                    <div className="space-y-4">
                        {filteredListings.map((listing) => (
                            <div
                                key={listing._id}
                                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                            >
                                <div className="flex items-start gap-6">
                                    {/* Image */}
                                    {listing.propertyData?.images?.[0] ? (
                                        <img
                                            src={listing.propertyData.images[0]}
                                            alt={listing.propertyData.title}
                                            className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                                        />
                                    ) : (
                                        <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <MapPin className="w-8 h-8 text-gray-400" />
                                        </div>
                                    )}

                                    {/* Content */}
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <h3 className="text-xl font-semibold text-gray-900 mb-1">
                                                    {listing.propertyData?.title || 'Untitled Property'}
                                                </h3>
                                                {listing.propertyData?.location && (
                                                    <div className="flex items-center gap-1 text-gray-600">
                                                        <MapPin className="w-4 h-4" />
                                                        <span>{listing.propertyData.location}</span>
                                                    </div>
                                                )}
                                            </div>
                                            <div className={`text-4xl font-bold ${getScoreColor(listing.analysis?.overallScore || 0)}`}>
                                                {listing.analysis?.overallScore || 0}
                                                <span className="text-sm text-gray-500">/100</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-6 text-sm text-gray-600 mb-4">
                                            {listing.propertyData?.price && (
                                                <div className="flex items-center gap-1">
                                                    <Euro className="w-4 h-4" />
                                                    <span className="font-semibold">{formatCurrency(listing.propertyData.price)}</span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-1">
                                                <Calendar className="w-4 h-4" />
                                                Saved {formatTimeAgo(listing.savedAt)}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                            <a
                                                href={listing.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-6 py-2 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                View Analysis
                                            </a>
                                            <button
                                                onClick={() => handleDelete(listing._id)}
                                                className="px-4 py-2 border border-red-300 text-red-600 font-semibold rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
